import React from 'react';
import { Link } from 'react-router-dom';
import SEOMetaTags from '../components/SEOMetaTags';
import CTASection from '../components/CTASection';
import ServicesStack from '../components/ServicesStack';
import StatsSection from '../components/StatsSection';
import ProcessSection from '../components/ProcessSection';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';

const AgencyHome = () => {
    const { t, currentLang } = useLanguage();
    usePageTitle('home');

    const getLangPath = (path) => {
        return currentLang && currentLang !== 'es' ? `/${currentLang}${path}` : path;
    };

    const stats = [
        { value: t('agency.stats.stat1.value'), label: t('agency.stats.stat1.label') },
        { value: t('agency.stats.stat2.value'), label: t('agency.stats.stat2.label') },
        { value: t('agency.stats.stat3.value'), label: t('agency.stats.stat3.label') },
        { value: t('agency.stats.stat4.value'), label: t('agency.stats.stat4.label') }
    ];

    const processSteps = [
        { title: t('agency.process.step1.title'), desc: t('agency.process.step1.desc') },
        { title: t('agency.process.step2.title'), desc: t('agency.process.step2.desc') },
        { title: t('agency.process.step3.title'), desc: t('agency.process.step3.desc') },
        { title: t('agency.process.step4.title'), desc: t('agency.process.step4.desc') }
    ];

    // Datos nativos para el ServicesStack de Rayo
    const servicesData = [
        {
            number: '01',
            title: t('agency.services.item1.title'),
            description: t('agency.services.item1.desc'),
            backgroundColor: 'services-stack-bg--agency',
            titleClass: 'opposite',
            descriptionClass: 't-opposite'
        },
        {
            number: '02',
            title: t('agency.services.item2.title'),
            description: t('agency.services.item2.desc'),
            backgroundColor: 'services-stack-bg--agency',
            titleClass: 'opposite',
            descriptionClass: 't-opposite'
        },
        {
            number: '03',
            title: t('agency.services.item3.title'),
            description: t('agency.services.item3.desc'),
            backgroundColor: 'services-stack-bg--agency',
            titleClass: 'opposite',
            descriptionClass: 't-opposite'
        },
        {
            number: '04',
            title: t('agency.services.item4.title'),
            description: t('agency.services.item4.desc'),
            backgroundColor: 'services-stack-bg--agency',
            titleClass: 'opposite',
            descriptionClass: 't-opposite'
        }
    ];

    return (
        <div className="inner-page-content" data-page="agency">
            <SEOMetaTags pageKey="home" />

            {/* =========================================================================
                1. HERO SECTION AGENCY (NATIVO DE LA PLANTILLA RAYO)
                ========================================================================= */}
            <div className="mxd-section mxd-section-inner-headline padding-text-pre-block mb-4 mb-md-5">
                <div className="mxd-container grid-container">
                    <div className="mxd-block loading-wrap">
                        <div className="container-fluid px-0">
                            <div className="row gx-0 align-items-center">
                                <div className="col-12 col-xl-2 mxd-grid-item no-margin">
                                    <div className="mxd-block__name name-inner-headline loading__item mb-4">
                                        <p className="mxd-point-subtitle">
                                            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20px" height="20px" viewBox="0 0 20 20" fill="currentColor">
                                                <path fill="currentColor" d="M19.6,9.6c0,0-3,0-4,0c-0.4,0-1.8-0.2-1.8-0.2c-0.6-0.1-1.1-0.2-1.6-0.6c-0.5-0.3-0.9-0.8-1.2-1.2c-0.3-0.4-0.4-0.9-0.5-1.4c0,0-0.1-1.1-0.2-1.5c-0.1-1.1,0-4.4,0-4.4C10.4,0.2,10.2,0,10,0S9.6,0.2,9.6,0.4c0,0,0.1,3.3,0,4.4c0,0.4-0.2,1.5-0.2,1.5C9.4,6.7,9.2,7.2,9,7.6C8.7,8.1,8.2,8.5,7.8,8.9c-0.5,0.3-1,0.5-1.6,0.6c0,0-1.2,0.1-1.7,0.2c-1,0.1-4.2,0-4.2,0C0.2,9.6,0,9.8,0,10c0,0.2,0.2,0.4,0.4,0.4c0,0,3.1-0.1,4.2,0c0.4,0,1.7,0.2,1.7,0.2c0.6,0.1,1.1,0.2,1.6,0.6c0.4,0.3,0.8,0.7,1.1,1.1c0.3,0.5,0.5,1,0.6,1.6c0,0,0.1,1.3,0.2,1.7c0,1,0,4.1,0,4.1c0,0.2,0.4-0.2,0.4-0.4s0.4-0.2,0.4-0.4c0,0,0-3.1,0-4.1c0-0.4,0.2-1.7,0.2-1.7c0.1-0.6,0.2-1.1,0.6-1.6c0.3-0.4,0.7-0.8,1.1-1.1c0.5-0.3,1-0.5,1.6-0.6c0,0,1.3-0.1,1.8-0.2c1,0,4,0,4,0c0.2,0,0.4-0.2,0.4-0.4C20,9.8,19.8,9.6,19.6,9.6L19.6,9.6z"/>
                                            </svg>
                                            <span>AGENCY</span>
                                        </p>
                                    </div>
                                </div>
                                <div className="col-12 col-xl-10 mxd-grid-item no-margin">
                                    <div className="mxd-block__content">
                                        <div className="mxd-block__inner-headline">
                                            <h1 className="inner-headline__title h1-small">
                                                <b>{t('agency.hero.title')}</b>
                                            </h1>
                                            <p className="t-large text-bright mt-4" style={{ color: '#2563eb' }}>
                                                {t('agency.hero.subtitle')}
                                            </p>
                                            <p className="inner-headline__text t-large text-muted">
                                                {t('agency.hero.description')}
                                            </p>
                                            <div className="mxd-promo__controls anim-uni-in-up mt-4 pt-2 d-flex flex-wrap gap-3">
                                                <Link
                                                    className="btn btn-anim btn-primary btn-large slide-right-up"
                                                    to={getLangPath('/agency/contact')}
                                                >
                                                    <span className="btn-caption">{t('agency.hero.cta_sell')}</span>
                                                    <i className="ph-bold ph-arrow-up-right"></i>
                                                </Link>
                                                <Link
                                                    className="btn btn-anim btn-outline btn-large slide-right-up"
                                                    to={getLangPath('/sell')}
                                                >
                                                    <span className="btn-caption">{t('agency.hero.cta_more')}</span>
                                                    <i className="ph-bold ph-arrow-up-right"></i>
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            {/* Cifras de confianza */}
            <StatsSection items={stats} />

            {/* Sección "Propiedades destacadas" oculta temporalmente (no renderizar) */}

            {/* =========================================================================
                3. SECCIÓN DE VENTAJAS - mxd-approach-list NATIVO DE RAYO
                ========================================================================= */}
            <div className="mxd-section padding-pre-grid my-4 my-md-5 py-3 py-md-4">
                <div className="mxd-container grid-container">
                    <div className="mxd-block">
                        <div className="mxd-section-title">
                            <div className="container-fluid p-0">
                                <div className="row g-0">
                                    <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                        <div className="mxd-section-title__hrtitle">
                                            <h2 className="reveal-type">{t('agency.advantages.title')}</h2>
                                        </div>
                                    </div>
                                    <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                        <div className="mxd-section-title__hrdescr">
                                            <p className="anim-uni-in-up">{t('agency.advantages.subtitle')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mxd-approach-list">
                            {/* Item 1 */}
                            <div className="mxd-approach-list__item">
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                                <div className="mxd-approach-list__inner">
                                    <div className="container-fluid px-0">
                                        <div className="row gx-0 align-items-center">
                                            <div className="col-12 col-xl-4 mxd-grid-item no-margin mb-3 mb-xl-0">
                                                <div className="mxd-approach-list__title anim-uni-in-up">
                                                    <span className="brand-tag brand-tag--agency me-2">01</span>
                                                    <h6>{t('agency.advantages.adv1.title')}</h6>
                                                </div>
                                            </div>
                                            <div className="col-12 col-xl-5 mxd-grid-item no-margin mb-3 mb-xl-0">
                                                <div className="mxd-approach-list__descr anim-uni-in-up">
                                                    <p>{t('agency.advantages.adv1.desc')}</p>
                                                </div>
                                            </div>
                                            <div className="col-12 col-xl-3 mxd-grid-item no-margin text-start text-xl-end mt-2 mt-xl-0">
                                                <Link to={getLangPath('/sell')} className="btn btn-anim btn-default btn-outline slide-right-up">
                                                    <span className="btn-caption">{t('agency.advantages.more')}</span>
                                                    <i className="ph-bold ph-arrow-up-right"></i>
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                            </div>

                            {/* Item 2 */}
                            <div className="mxd-approach-list__item">
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                                <div className="mxd-approach-list__inner">
                                    <div className="container-fluid px-0">
                                        <div className="row gx-0 align-items-center">
                                            <div className="col-12 col-xl-4 mxd-grid-item no-margin mb-3 mb-xl-0">
                                                <div className="mxd-approach-list__title anim-uni-in-up">
                                                    <span className="brand-tag brand-tag--agency me-2">02</span>
                                                    <h6>{t('agency.advantages.adv2.title')}</h6>
                                                </div>
                                            </div>
                                            <div className="col-12 col-xl-5 mxd-grid-item no-margin mb-3 mb-xl-0">
                                                <div className="mxd-approach-list__descr anim-uni-in-up">
                                                    <p>{t('agency.advantages.adv2.desc')}</p>
                                                </div>
                                            </div>
                                            <div className="col-12 col-xl-3 mxd-grid-item no-margin text-start text-xl-end mt-2 mt-xl-0">
                                                <Link to={getLangPath('/sell')} className="btn btn-anim btn-default btn-outline slide-right-up">
                                                    <span className="btn-caption">{t('agency.advantages.more')}</span>
                                                    <i className="ph-bold ph-arrow-up-right"></i>
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                            </div>

                            {/* Item 3 */}
                            <div className="mxd-approach-list__item">
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                                <div className="mxd-approach-list__inner">
                                    <div className="container-fluid px-0">
                                        <div className="row gx-0 align-items-center">
                                            <div className="col-12 col-xl-4 mxd-grid-item no-margin mb-3 mb-xl-0">
                                                <div className="mxd-approach-list__title anim-uni-in-up">
                                                    <span className="brand-tag brand-tag--agency me-2">03</span>
                                                    <h6>{t('agency.advantages.adv3.title')}</h6>
                                                </div>
                                            </div>
                                            <div className="col-12 col-xl-5 mxd-grid-item no-margin mb-3 mb-xl-0">
                                                <div className="mxd-approach-list__descr anim-uni-in-up">
                                                    <p>{t('agency.advantages.adv3.desc')}</p>
                                                </div>
                                            </div>
                                            <div className="col-12 col-xl-3 mxd-grid-item no-margin text-start text-xl-end mt-2 mt-xl-0">
                                                <Link to={getLangPath('/sell')} className="btn btn-anim btn-default btn-outline slide-right-up">
                                                    <span className="btn-caption">{t('agency.advantages.more')}</span>
                                                    <i className="ph-bold ph-arrow-up-right"></i>
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* =========================================================================
                4. NUESTROS SERVICIOS 360º - STACK DE SERVICIOS NATIVO DE RAYO
                ========================================================================= */}
            <div className="mxd-section padding-pre-grid my-4 my-md-5 py-3 py-md-4">
                <div className="mxd-container grid-container">
                    <div className="mxd-block">
                        <div className="mxd-section-title">
                            <div className="container-fluid p-0">
                                <div className="row g-0">
                                    <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                        <div className="mxd-section-title__hrtitle">
                                            <h2 className="reveal-type">{t('agency.services.title')}</h2>
                                        </div>
                                    </div>
                                    <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                        <div className="mxd-section-title__hrdescr">
                                            <p className="anim-uni-in-up">{t('agency.services.subtitle')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <ServicesStack services={servicesData} />
                    </div>
                </div>
            </div>

            {/* Proceso de trabajo en 4 pasos */}
            <ProcessSection
                title={t('agency.process.title')}
                subtitle={t('agency.process.subtitle')}
                steps={processSteps}
            />

            {/* =========================================================================
                5. INTEGRACIÓN DE PORTALES INMOBILIARIOS (FOTOCASA NATIVO DE RAYO)
                ========================================================================= */}
            {/* Sección Fotocasa oculta temporalmente (no renderizar) */}

            {/* =========================================================================
                7. SECCIÓN CTA FINAL (CTASECTION NATIVO DE RAYO)
                ========================================================================= */}
            <CTASection
                title={t('home.cta.title')}
                description={t('home.cta.description')}
                buttonText={t('home.cta.button')}
                buttonTo={getLangPath('/agency/contact')}
                icon="/img/icons/300x300_obj-cta-01.webp"
                iconType="image"
            />
        </div>
    );
};

export default AgencyHome;
